import _pick from 'lodash/pick';
import Config from '../../config';
import { METHODS } from '../constants';
import { QueryKeys } from '../queries';
import { useCustomMutation } from './index';

export const useUpdateScenario = onSuccess =>
  useCustomMutation({
    handleData: scenario => ({
      url: Config.endpoints.UPDATE_SCENARIO
        .replace(/:scenarioId/, scenario.scenarioId),
      method: METHODS.PATCH,
      body: {
        scenarioName: scenario.scenarioName,
        publicFlag: scenario.publicFlag,
        lockedFlag: scenario.lockedFlag,
      },
    }),
    resetKey: QueryKeys.scenarios.ScenariosList,
    errorMessage: 'Saving scenario failed',
    successMessage: 'Scenario updated successfully',
    onSuccess,
  });

export const useAddScenario = onSuccess =>
  useCustomMutation({
    handleData: scenario => ({
      url: Config.endpoints.CREATE_SCENARIO,
      method: METHODS.POST,
      body: scenario,
    }),
    resetKey: QueryKeys.scenarios.ScenariosList,
    errorMessage: 'Creating scenario failed',
    successMessage: 'Scenario added successfully',
    onSuccess,
  });

export const useDeleteScenario = onSuccess =>
  useCustomMutation({
    handleData: scenario => ({
      url: Config.endpoints.REMOVE_SCENARIO
        .replace(/:scenarioId/, scenario.scenarioId),
      method: METHODS.DELETE,
    }),
    invalidateKey: QueryKeys.scenarios.ScenariosList,
    errorMessage: 'Removing scenario failed',
    successMessage: 'Scenario removed successfully',
    onSuccess,
  });

export const useUpdateScenarioDetail = (
  {
    reportName,
    fieldName,
    countryTaxType,
  },
  onSuccess
) =>
  useCustomMutation({
    handleData: ({
      scenarioId,
      detail: {
        detailId,
        detailName,
        value,
        companyCode,
        account,
      },
    }) => ({
      url: Config.endpoints.EDIT_SCENARIO_DETAIL
        .replace(/:scenarioId/, scenarioId)
        .replace(/:reportName/, reportName)
        .replace(/:detailId/, detailId)
        .replace(/:countryTaxType/, countryTaxType)
        .replace(/:companyCode/, companyCode)
        .replace(/:fieldName/, fieldName),
      method: METHODS.PATCH,
      body: {
        detailName,
        value,
        account,
      },
    }),
    resetKey: query => [
      QueryKeys.scenarios.ScenarioDetailsList,
      QueryKeys.scenarios.ScenarioReportsData,
      QueryKeys.scenarios.ScenarioReportsCompareData,
    ].includes(query.queryKey[0]),
    errorMessage: 'Updating scenario detail failed',
    successMessage: 'Scenario detail updated successfully',
    onSuccess,
  });

export const useCreateScenarioDetail = (
  {
    reportName,
    fieldName,
  },
  onSuccess
) =>
  useCustomMutation({
    handleData: ({ detail, scenarioId }) => ({
      url: Config.endpoints.CREATE_SCENARIO_DETAIL
        .replace(/:scenarioId/, scenarioId)
        .replace(/:reportName/, reportName)
        .replace(/:fieldName/, fieldName),
      method: METHODS.POST,
      body: {
        companyCode: detail.companyCode,
        countryTaxType: detail.countryTaxType,
        detailName: detail.detailName,
        value: detail.value,
        account: detail.account,
        accountExceptionId: detail.accountExceptionId,
      },
    }),
    resetKey: query => [
      QueryKeys.scenarios.ScenarioDetailsList,
      QueryKeys.scenarios.ScenarioReportsData,
      QueryKeys.scenarios.ScenarioReportsCompareData,
    ].includes(query.queryKey[0]),
    errorMessage: 'Creating scenario detail failed',
    successMessage: 'Scenario detail created successfully',
    onSuccess,
  });

export const useCreateScenarioDetailPair = (
  {
    reportName,
    fieldName,
  },
  onSuccess
) =>
  useCustomMutation({
    handleData: ({ detail, baseScenarioId, compareScenarioId }) => ({
      url: Config.endpoints.CREATE_SCENARIO_DETAIL_PAIR
        .replace(/:baseScenarioId/, baseScenarioId)
        .replace(/:compareScenarioId/, compareScenarioId)
        .replace(/:reportName/, reportName)
        .replace(/:fieldName/, fieldName),
      method: METHODS.POST,
      body: {
        companyCode: detail.companyCode,
        countryTaxType: detail.countryTaxType,
        detailName: detail.detailName,
        baseDetailValue: detail.value,
        compareDetailValue: detail.compareValue,
        account: detail.account,
        scenarioExceptionId: detail.scenarioExceptionId,
      },
    }),
    resetKey: query => [
      QueryKeys.scenarios.ScenarioDetailsList,
      QueryKeys.scenarios.ScenarioReportsData,
      QueryKeys.scenarios.ScenarioReportsCompareData,
    ].includes(query.queryKey[0]),
    errorMessage: 'Creating scenario detail failed',
    successMessage: 'Scenario detail created successfully',
    onSuccess,
  });

export const useDeleteScenarioDetail = (
  {
    reportName,
    fieldName,
    countryTaxType,
  },
  onSuccess
) =>
  useCustomMutation({
    handleData: ({
      scenarioId,
      detailId,
      companyCode,
    }) => ({
      url: Config.endpoints.REMOVE_SCENARIO_DETAIL
        .replace(/:scenarioId/, scenarioId)
        .replace(/:reportName/, reportName)
        .replace(/:detailId/, detailId)
        .replace(/:countryTaxType/, countryTaxType)
        .replace(/:companyCode/, companyCode)
        .replace(/:fieldName/, fieldName),
      method: METHODS.DELETE,
    }),
    resetKey: query => [
      QueryKeys.scenarios.ScenarioDetailsList,
      QueryKeys.scenarios.ScenarioReportsData,
      QueryKeys.scenarios.ScenarioReportsCompareData,
    ].includes(query.queryKey[0]),
    errorMessage: 'Deleting scenario detail failed',
    successMessage: 'Scenario detail deleted successfully',
    onSuccess,
  });

/* Scenario Members mutations */

export const useAddScenarioMember = ({
  scenarioId,
}, onSuccess) => useCustomMutation({
  handleData: ({ scenarioMember }) => ({
    url: Config.endpoints.ADD_SCENARIO_MEMBER
      .replace(/:scenarioId/, scenarioId),
    method: METHODS.POST,
    body: scenarioMember,
  }),
  resetKey: query => [
    QueryKeys.scenariosMembers.MembersList,
    QueryKeys.scenarios.ScenarioDetailsList,
    QueryKeys.scenarios.ScenarioReportsData,
    QueryKeys.scenarios.ScenarioReportsCompareData,
  ].includes(query.queryKey[0]),
  errorMessage: 'Adding member failed',
  successMessage: 'Member added successfully',
  onSuccess,
});

export const useEditScenarioMember = ({
  scenarioId,
}, onSuccess) => useCustomMutation({
  handleData: ({ scenarioMember }) => ({
    url: Config.endpoints.EDIT_SCENARIO_MEMBER
      .replace(/:scenarioId/, scenarioId)
      .replace(/:companyCode/, scenarioMember.companyCode),
    method: METHODS.PUT,
    body: _pick(scenarioMember, [
      'scenarioEnabled',
      'companyCodeDescription',
      'legalEntity',
      'businessUnit',
      'associatedLeCountryCode',
      'recordFTC',
      'subledgerFlag',
      'functionalCurrency',
      'periodName',
      'overrideTaxCurrency',
      'statCalc',
      'statCurrency',
      'overrideSecondaryTax',
      'businessCode',
      'connectionName',
    ]),
  }),
  resetKey: query => [
    QueryKeys.scenariosMembers.MembersList,
    QueryKeys.scenarios.ScenarioDetailsList,
    QueryKeys.scenarios.ScenarioReportsData,
    QueryKeys.scenarios.ScenarioReportsCompareData,
  ].includes(query.queryKey[0]),
  errorMessage: 'Updating member failed',
  successMessage: 'Member updated successfully',
  onSuccess,
});

export const useDeleteScenarioMember = ({
  scenarioId,
}, onSuccess) => useCustomMutation({
  handleData: ({ entityToRemove }) => ({
    url: Config.endpoints.DELETE_SCENARIO_MEMBER
      .replace(/:scenarioId/, scenarioId)
      .replace(/:companyCode/, entityToRemove.companyCode),
    method: METHODS.DELETE,
  }),
  resetKey: query => [
    QueryKeys.scenariosMembers.MembersList,
    QueryKeys.scenarios.ScenarioDetailsList,
    QueryKeys.scenarios.ScenarioReportsData,
    QueryKeys.scenarios.ScenarioReportsCompareData,
  ].includes(query.queryKey[0]),
  errorMessage: 'Deleting Company Code failed',
  successMessage: 'Company Code removed successfully',
  onSuccess,
});

export const useAssignMembersToScenario = ({
  scenarioId,
}) => useCustomMutation({
  handleData: members => ({
    url: Config.endpoints.ASSIGN_COMPANY_CODES_TO_SCENARIO
      .replace(/:scenarioId/, scenarioId),
    method: METHODS.POST,
    body: members,
  }),
  resetKey: query => [
    QueryKeys.scenariosMembers.MembersList,
    QueryKeys.scenarios.ScenarioDetailsList,
    QueryKeys.scenarios.ScenarioReportsData,
    QueryKeys.scenarios.ScenarioReportsCompareData,
  ].includes(query.queryKey[0]),
  errorMessage: 'Assigning members to scenario failed',
  successMessage: 'Members assigned to scenario successfully',
});

export const useDisassociateMembersFromScenario = ({
  scenarioId,
}) => useCustomMutation({
  handleData: members => ({
    url: Config.endpoints.DISASSOCIATE_COMPANY_CODE_FROM_SCENARIO
      .replace(/:scenarioId/, scenarioId),
    method: METHODS.DELETE,
    body: members,
  }),
  resetKey: query => [
    QueryKeys.scenariosMembers.MembersList,
    QueryKeys.scenarios.ScenarioDetailsList,
    QueryKeys.scenarios.ScenarioReportsData,
    QueryKeys.scenarios.ScenarioReportsCompareData,
  ].includes(query.queryKey[0]),
  errorMessage: 'Disassociating members to scenario failed',
  successMessage: 'Members disassociated from scenario successfully',
});


export const useAddScenarioObligation = ({ scenarioId }, onSuccess) =>
  useCustomMutation({
    handleData: ({ scenarioObligation }) => ({
      url: Config.endpoints.ADD_SCENARIO_OBLIGATION
        .replace(/:scenarioId/, scenarioId),
      method: 'POST',
      body: scenarioObligation,
    }),
    resetKey: query => [
      QueryKeys.scenariosObligations.ObligationsList,
    ].includes(query.queryKey[0]),
    onSuccess,
    errorMessage: 'Adding obligation failed',
    successMessage: 'Obligation added successfully',
  });

export const useUpdateScenarioObligation = ({ scenarioId }, onSuccess) =>
  useCustomMutation({
    handleData: ({ scenarioObligation: { companyCode, countryCode, countryTaxType, ...obligation } }) => ({
      url: Config.endpoints.UPDATE_SCENARIO_OBLIGATION
        .replace(/:scenarioId/, scenarioId)
        .replace(/:companyCode/, companyCode)
        .replace(/:countryCode/, countryCode)
        .replace(/:countryTaxType/, countryTaxType),
      method: 'PUT',
      body: _pick(obligation, [
        'periodName',
        'overrideTaxCurrency',
        'federalFTCLimit',
        'overrideTaxRate',
        'overrideDefTaxRate',
        'NOL',
        'recordVA',
        'stateApportionmentRate',
        'stateMinimumTax',
        'statePrePostNOL',
      ]),
    }),
    invalidateKey: query => [
      QueryKeys.scenariosObligations.ObligationsList,
    ].includes(query.queryKey[0]),
    onSuccess,
    errorMessage: 'Updating obligation failed',
    successMessage: 'Obligation updated successfully',
  });


export const useDeleteScenarioObligation = ({ scenarioId }, onSuccess) =>
  useCustomMutation({
    handleData: ({ entityToRemove }) => ({
      url: Config.endpoints.DELETE_SCENARIO_OBLIGATION
        .replace(/:scenarioId/, scenarioId)
        .replace(/:companyCode/, entityToRemove.companyCode)
        .replace(/:countryCode/, entityToRemove.countryCode)
        .replace(/:countryTaxType/, entityToRemove.countryTaxType),
      method: 'DELETE',
    }),
    resetKey: query => [
      QueryKeys.scenariosObligations.ObligationsList,
    ].includes(query.queryKey[0]),
    onSuccess,
    errorMessage: 'Removing obligation failed',
    successMessage: 'Obligation removed successfully',
  });


export const useBatchProcessDetails = ({
  successMessage = 'Details changes saved successfully',
  onSuccess,
} = {}) =>
  useCustomMutation({
    handleData: saveData => ({
      url: Config.endpoints.BATCH_SCENARIO_DETAILS,
      method: 'POST',
      body: saveData,
    }),
    resetKey: query => [
      QueryKeys.scenarios.ScenarioRowDetailsList,
      QueryKeys.scenarios.ScenarioReportsData,
      QueryKeys.scenarios.ScenarioReportsCompareData,
    ].includes(query.queryKey[0]),
    onSuccess,
    errorMessage: 'Failed to save details changes',
    successMessage,
  });

export const useBatchRemoveDetails = ({ onSuccess }) =>
  useBatchProcessDetails({
    successMessage: 'Details removed successfully',
    onSuccess,
  });
